import { executeMutation } from 'firebase/data-connect';
import { componentUpdateFilingByIdRef, componentUpdateLifecycleByIdRef } from '@jtms/default-connector';
import type { componentStatuses, ComponentShipment, Dimensionals } from '@dis/models';

export interface ComponentFilingUpdate {
  traceUrl?: string | null;
  traceNumber?: string | null;
  lastNde?: string | null;
  lastNdeInspection?: Date | null;
  inventoryStatus?: string | null;
  statuses?: componentStatuses | null;
  rack?: string | null;
}

export interface ComponentLifecycleUpdate {
  inventoryStatus?: string | null;
  statuses?: componentStatuses | null;
  latestAssignedBuild?: string | null;
  rack?: string | null;
  shelf?: string | null;
  slot?: string | null;
  bin?: string | null;
  shipments?: ComponentShipment[] | null;
  dimensionals?: Dimensionals | null;
  lotQty?: number | null;
  orderedQty?: number | null;
}

// YYYY-MM-DD in local time, so a date picked at local midnight doesn't land on the day before in UTC.
function toLocalDateString(date: Date): string {
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${date.getFullYear()}-${month}-${day}`;
}

// Shared by SerialNumberFileService (ScanManager) and InspectionFileManagerService — both
// filing flows only ever touch this handful of fields on an already-known Component row, so
// they call this instead of hand-rolling the executeMutation + Ref call twice.
export async function upsertComponentFilingFieldsById(
  id: string,
  serialNumber: string,
  partNumber: string,
  updates: ComponentFilingUpdate
): Promise<boolean> {
  try {
    await executeMutation(
      componentUpdateFilingByIdRef({
        id,
        serialNumber,
        partNumberString: partNumber,
        traceUrlString: updates.traceUrl ?? undefined,
        traceNumberString: updates.traceNumber ?? undefined,
        lastNdeUrlString: updates.lastNde ?? undefined,
        lastNdeInspection: updates.lastNdeInspection ? updates.lastNdeInspection.toISOString() : undefined,
        lastNdeInspectionDate: updates.lastNdeInspection ? toLocalDateString(updates.lastNdeInspection) : undefined,
        inventoryStatusString: updates.inventoryStatus ?? undefined,
        statusesJson: updates.statuses ?? undefined,
        rackString: updates.rack ?? undefined,
      })
    );
    return true;
  } catch (error) {
    console.error(`upsertComponentFilingFieldsById: failed to update component "${id}" in Data Connect`, error);
    return false;
  }
}

// Shared by assembler/job/shipment/ship-n-receiving/inventory flows — they all update some
// subset of the same handful of "where is this component in its lifecycle" fields, so they call
// this instead of each getting a near-duplicate mutation.
export async function upsertComponentLifecycleFieldsById(
  id: string,
  serialNumber: string,
  partNumber: string,
  updates: ComponentLifecycleUpdate
): Promise<void> {
  try {
    await executeMutation(
      componentUpdateLifecycleByIdRef({
        id,
        serialNumber,
        partNumberString: partNumber,
        inventoryStatusString: updates.inventoryStatus ?? undefined,
        statusesJson: updates.statuses ?? undefined,
        latestAssignedBuildString: updates.latestAssignedBuild ?? undefined,
        rackString: updates.rack ?? undefined,
        shelfString: updates.shelf ?? undefined,
        slotString: updates.slot ?? undefined,
        binString: updates.bin ?? undefined,
        shipmentsJson: updates.shipments ?? undefined,
        dimensionalsJson: updates.dimensionals ?? undefined,
        lotQty: updates.lotQty ?? undefined,
        orderedQty: updates.orderedQty ?? undefined,
      })
    );
  } catch (error) {
    console.error(`upsertComponentLifecycleFieldsById: failed to update component "${id}" in Data Connect`, error);
  }
}
